import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Package, Clock, Landmark, Banknote } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { api } from '../api';
import { formatMoney } from '../utils/money';

export default function OrderConfirmationPage() {
  const { id } = useParams();
  const location = useLocation();
  const { settings } = useSettings();
  const money = (v) => formatMoney(v, settings);

  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!order);
  const [error, setError] = useState('');

  useEffect(() => {
    if (order) return;
    const email = location.state?.email;
    api
      .getOrder(id, email)
      .then((res) => setOrder(res.order || res))
      .catch((err) => setError(err.message || 'Order not found'))
      .finally(() => setLoading(false));
  }, [id, order, location.state]);

  if (loading) {
    return <Centered><p style={{ color: 'var(--mid)' }}>Loading your order…</p></Centered>;
  }
  if (error || !order) {
    return (
      <Centered>
        <h2 style={{ fontFamily: 'Syne', color: 'var(--forest)', marginBottom: '10px' }}>We couldn't find that order</h2>
        <p style={{ color: 'var(--mid)', marginBottom: '22px' }}>{error}</p>
        <Link to="/shop" style={btn}>Continue shopping</Link>
      </Centered>
    );
  }

  const num = order.orderNumber || order.id || order._id;
  const method = order.paymentMethod;
  const items = order.items || [];

  const methodNote = {
    cod: { Icon: Banknote, text: 'Pay with cash when your order is delivered.' },
    bank_transfer: { Icon: Landmark, text: 'Bank transfer details have been emailed to you. Your order ships once payment is received.' },
    stripe: { Icon: Package, text: 'Your card payment was received. Your order is being prepared.' },
  }[method] || { Icon: Package, text: 'Your order is being prepared.' };

  return (
    <div style={{ paddingTop: '68px', minHeight: '100vh', background: 'var(--cream)' }}>
      <div style={{ maxWidth: '680px', margin: '0 auto', padding: '56px 24px 90px' }}>
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          style={{ textAlign: 'center', marginBottom: '34px' }}>
          <div style={{ width: '76px', height: '76px', borderRadius: '50%', background: '#e8f5ec', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '18px' }}>
            <CheckCircle2 size={40} color="var(--accent-green)" />
          </div>
          <h1 style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '2rem', color: 'var(--forest)', marginBottom: '8px' }}>
            Thank you!
          </h1>
          <p style={{ color: 'var(--mid)' }}>Your order <strong style={{ color: 'var(--charcoal)' }}>#{num}</strong> is confirmed.</p>
        </motion.div>

        <div style={{ background: '#fff', border: '1px solid var(--mist)', borderRadius: '16px', padding: '18px 20px', display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '22px' }}>
          <methodNote.Icon size={22} color="var(--forest)" />
          <span style={{ fontSize: '0.88rem', color: 'var(--charcoal)' }}>{methodNote.text}</span>
        </div>

        <div style={{ background: '#fff', border: '1px solid var(--mist)', borderRadius: '18px', padding: '26px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
            <h3 style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '1.05rem', color: 'var(--charcoal)' }}>Order details</h3>
            <StatusPill status={order.status || 'pending'} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '18px' }}>
            {items.map((it, i) => (
              <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '10px', overflow: 'hidden', background: it.gradient || 'var(--smoke)', flexShrink: 0 }}>
                  {it.image && <img src={it.image} alt={it.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--charcoal)' }}>{it.name}</div>
                  <div style={{ fontSize: '0.76rem', color: 'var(--mid)' }}>Qty {it.qty}</div>
                </div>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--forest)' }}>{money(it.price * it.qty)}</div>
              </div>
            ))}
          </div>

          <div style={{ borderTop: '1px solid var(--mist)', paddingTop: '16px' }}>
            <Row label="Subtotal" value={money(order.subtotal)} />
            <Row label="Shipping" value={order.shipping === 0 ? 'FREE' : money(order.shipping)} />
            {order.tax > 0 && <Row label="Tax" value={money(order.tax)} />}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'Syne', fontWeight: 700, fontSize: '1.1rem', marginTop: '8px' }}>
              <span>Total</span><span style={{ color: 'var(--forest)' }}>{money(order.total)}</span>
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '30px' }}>
          <Link to="/shop" style={btn}>Continue shopping</Link>
        </div>
      </div>
    </div>
  );
}

const btn = {
  display: 'inline-flex', gap: '8px', alignItems: 'center', background: 'var(--forest)',
  color: '#fff', padding: '13px 26px', borderRadius: '10px', fontFamily: 'Syne', fontWeight: 700,
  textDecoration: 'none', fontSize: '0.85rem', letterSpacing: '0.04em', textTransform: 'uppercase',
};

const Centered = ({ children }) => (
  <div style={{ paddingTop: '120px', minHeight: '70vh', background: 'var(--cream)', textAlign: 'center', padding: '120px 24px' }}>{children}</div>
);

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--mid)', marginBottom: '8px' }}>
    <span>{label}</span><span>{value}</span>
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    pending: { bg: '#fff4e0', fg: '#b8860b', Icon: Clock, text: 'Pending' },
    paid: { bg: '#e8f5ec', fg: '#2e7d4f', Icon: CheckCircle2, text: 'Paid' },
    processing: { bg: '#e7f0fb', fg: '#2b6cb0', Icon: Package, text: 'Processing' },
    shipped: { bg: '#e7f0fb', fg: '#2b6cb0', Icon: Package, text: 'Shipped' },
    delivered: { bg: '#e8f5ec', fg: '#2e7d4f', Icon: CheckCircle2, text: 'Delivered' },
    cancelled: { bg: '#fdecec', fg: '#c0392b', Icon: Clock, text: 'Cancelled' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{ display: 'inline-flex', gap: '5px', alignItems: 'center', background: s.bg, color: s.fg, fontSize: '0.72rem', fontWeight: 700, padding: '5px 11px', borderRadius: '999px', textTransform: 'capitalize' }}>
      <s.Icon size={12} /> {s.text}
    </span>
  );
};
